import { base44 } from '@/api/base44Client';

/**
 * Helper function to create notifications for users
 * This will work for in-app notifications immediately
 * When backend functions are enabled, extend this to send push notifications
 */
export async function createNotification({ userId, type, title, message, referenceId = null, referenceType = null, actionUrl = null, icon = null }) {
  try {
    // Check user preferences
    const prefs = await base44.entities.NotificationPreference.filter({ user_id: userId });
    const userPrefs = prefs[0];

    // Check if user wants this type of notification
    if (userPrefs) {
      const prefKey = `notify_${type.replace('_', '_')}`;
      if (userPrefs[prefKey] === false) {
        return null; // User has disabled this notification type
      }

      // Check quiet hours
      if (userPrefs.quiet_hours_start && userPrefs.quiet_hours_end) {
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes();
        const [startHour, startMin] = userPrefs.quiet_hours_start.split(':').map(Number);
        const [endHour, endMin] = userPrefs.quiet_hours_end.split(':').map(Number);
        const startTime = startHour * 60 + startMin;
        const endTime = endHour * 60 + endMin;

        if (currentTime >= startTime && currentTime <= endTime) {
          return null; // Within quiet hours
        }
      }
    }

    // Create in-app notification
    const notification = await base44.entities.Notification.create({
      user_id: userId,
      type,
      title,
      message,
      reference_id: referenceId,
      reference_type: referenceType,
      action_url: actionUrl,
      icon,
      read: false,
    });

    // TODO: When backend functions are enabled, add push notification logic here
    // if (userPrefs && userPrefs.push_enabled && userPrefs.push_token) {
    //   await sendPushNotification(userPrefs.push_token, { title, message, data: { notificationId: notification.id } });
    // }

    return notification;
  } catch (error) {
    console.error('Error creating notification:', error);
    return null;
  }
}

/**
 * Notify user when someone replies to their comment
 */
export async function notifyCommentReply({ commentAuthorId, postTitle, replierName, postId }) {
  return createNotification({
    userId: commentAuthorId,
    type: 'comment_reply',
    title: 'Nueva respuesta',
    message: `${replierName} respondió a tu comentario en "${postTitle}"`,
    referenceId: postId,
    referenceType: 'post',
    actionUrl: `/Community?post=${postId}`,
    icon: '💬',
  });
}

/**
 * Notify user when someone likes their post
 */
export async function notifyPostLike({ postAuthorId, postTitle, likerName, postId }) {
  return createNotification({
    userId: postAuthorId,
    type: 'post_like',
    title: 'Nuevo like',
    message: `A ${likerName} le gustó tu post "${postTitle}"`,
    referenceId: postId,
    referenceType: 'post',
    actionUrl: `/Community?post=${postId}`,
    icon: '❤️',
  });
}

/**
 * Notify group members about new posts
 */
export async function notifyNewPostInGroup({ groupId, postTitle, authorName, postId }) {
  try {
    const memberships = await base44.entities.GroupMembership.filter({ group_id: groupId });
    
    // Notify all members except the author
    const notifications = memberships
      .filter(m => m.created_by !== authorName) // Don't notify the author
      .map(m => createNotification({
        userId: m.user_id,
        type: 'new_post_in_group',
        title: 'Nuevo post en tu grupo',
        message: `${authorName} publicó: "${postTitle}"`,
        referenceId: postId,
        referenceType: 'post',
        actionUrl: `/Community?post=${postId}`,
        icon: '👥',
      }));

    await Promise.all(notifications);
  } catch (error) {
    console.error('Error notifying group members:', error);
  }
}

/**
 * Notify user about product updates
 */
export async function notifyProductUpdate({ userId, productName, updateType, productId }) {
  return createNotification({
    userId,
    type: 'product_update',
    title: 'Actualización de producto',
    message: `"${productName}" tiene una ${updateType}`,
    referenceId: productId,
    referenceType: 'product',
    actionUrl: `/Profile`,
    icon: '📦',
  });
}

/**
 * Notify user about health reminders based on their profile
 */
export async function notifyHealthReminder({ userId, reminderType, message }) {
  return createNotification({
    userId,
    type: 'health_reminder',
    title: 'Recordatorio de salud',
    message,
    icon: '🔔',
  });
}

/**
 * Notify user about milestone achievements
 */
export async function notifyMilestone({ userId, milestoneName, reward }) {
  return createNotification({
    userId,
    type: 'milestone_achieved',
    title: '¡Logro desbloqueado! 🎉',
    message: `Alcanzaste el hito: ${milestoneName}. +${reward} puntos`,
    actionUrl: `/Rewards`,
    icon: '🏆',
  });
}

/**
 * Notify user about available rewards
 */
export async function notifyRewardAvailable({ userId, rewardName, pointsCost }) {
  return createNotification({
    userId,
    type: 'reward_available',
    title: 'Nueva recompensa disponible',
    message: `Puedes canjear: ${rewardName} (${pointsCost} puntos)`,
    actionUrl: `/Rewards`,
    icon: '🎁',
  });
}